import { useMemo } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table, {
  SortingTypes,
} from '@decisiv/ui-components/lib/components/Table';

import { SortableState } from '@decisiv/ui-components/lib/components/Table/hooks/useSortableHeaders';

import PaginationFooter from 'components/PaginationFooter';
import useFooter from 'utils/useFooter';

import useTeamsColumns, {
  ColumnName,
  TeamTableColumns,
} from './hooks/useTeamsColumns';

const FullHeightDiv = styled.div<{ footerHeight: number }>`
  position: absolute;
  width: 100%;
  height: calc(100% - ${({ footerHeight }) => footerHeight}px);
`;

function generateDeletionAlert(sentinelTeamNames: (string | undefined)[]) {
  if (!sentinelTeamNames || sentinelTeamNames.length === 0) return undefined;
  if (sentinelTeamNames.length === 1) {
    const teamName = sentinelTeamNames[0];

    return t`${teamName} team will be deleted soon.`;
  }

  const commaSeparatedTeams = sentinelTeamNames
    .slice(0, sentinelTeamNames.length - 1)
    .join(', ');
  const lastTeam = sentinelTeamNames.at(-1);

  return t`${commaSeparatedTeams} and ${lastTeam} teams will be deleted soon.`;
}

type Props = {
  teams: TeamTableColumns[];
  onSort: (column: string, direction: SortingTypes) => void;
  setPage: (page: number) => void;
  defaultSort?: SortableState;
  pagination?: Pagination;
  except?: ColumnName[];
  order?: ColumnName[];
};

export default function TeamsList({
  teams,
  setPage,
  pagination,
  onSort,
  defaultSort,
  except = [],
  order = undefined,
}: Props) {
  const columns = useTeamsColumns({ onSort, except, order });

  const { footerHeight, footerRef } = useFooter();

  const teamsWithAlerts = useMemo(
    () =>
      teams
        ?.filter((sentinelTeam) => sentinelTeam.scheduledForDeletionAt)
        ?.map((sentinelTeam) => sentinelTeam.name),
    [teams],
  );

  const alertText = useMemo(
    () => generateDeletionAlert(teamsWithAlerts),
    [teamsWithAlerts],
  );

  return (
    <>
      <Flex paddingLeft={2.2} paddingRight={2} marginBottom={1.3}>
        {alertText && <Alert intent="danger" title={alertText} />}
      </Flex>
      <FullHeightDiv footerHeight={footerHeight}>
        <Table
          columns={columns}
          data={teams}
          defaultSort={defaultSort}
          getRowKey={({ id }) => id}
          height="100%"
          footer={() => (
            <PaginationFooter
              ref={footerRef}
              title={t`Teams`}
              setCurrentPage={setPage}
              totalOnPage={teams.length}
              page={pagination?.page}
              perPage={pagination?.perPage}
              totalEntries={pagination?.totalEntries}
              totalPages={pagination?.totalPages}
            />
          )}
          scroll={['x', 'y']}
        />
      </FullHeightDiv>
    </>
  );
}
